import React, { useState, useEffect, useRef } from "react";
import { BLOG_API } from "../config/api";
import "../styles/AdminBlog.css";

/* ========================================================
   RICH TEXT VISUAL WYSIWYG EDITOR (HIDES RAW HTML TAGS)
   ======================================================== */
const RichEditor = ({ value, onChange, placeholder = "Start typing your article here..." }) => {
  const editorRef = useRef(null);
  const [isCodeView, setIsCodeView] = useState(false);
  const [internalHtml, setInternalHtml] = useState(value || "");

  // Update editor innerHTML only when value changes externally
  useEffect(() => {
    setInternalHtml(value || "");
    if (editorRef.current && !isCodeView) {
      if (editorRef.current.innerHTML !== (value || "")) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value, isCodeView]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setInternalHtml(html);
      onChange(html);
    }
  };

  const handleCodeChange = (e) => {
    const html = e.target.value;
    setInternalHtml(html);
    onChange(html);
  };

  const executeCmd = (cmd, arg = null) => {
    if (isCodeView) return;
    document.execCommand(cmd, false, arg);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  const handleFormatBlock = (tag) => {
    if (isCodeView) return;
    document.execCommand("formatBlock", false, tag);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  const handleInsertLink = () => {
    const url = prompt("Enter website link (URL):", "https://");
    if (url) {
      executeCmd("createLink", url);
    }
  };

  // Word count & approximate read time calculation
  const cleanText = (internalHtml || "").replace(/<[^>]*>/g, " ").trim();
  const wordCount = cleanText ? cleanText.split(/\s+/).filter(Boolean).length : 0;
  const readTimeMins = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="rich-editor-wrapper">
      {/* TOOLBAR */}
      <div className="rich-editor-toolbar" role="toolbar" aria-label="Text Formatting">
        <div className="toolbar-btn-group">
          <select
            className="toolbar-select"
            onChange={(e) => {
              handleFormatBlock(e.target.value);
              e.target.value = "";
            }}
            defaultValue=""
            disabled={isCodeView}
            title="Heading Style"
          >
            <option value="" disabled>Text Style</option>
            <option value="p">Paragraph (Normal)</option>
            <option value="h2">Heading 2 (Main Section)</option>
            <option value="h3">Heading 3 (Subsection)</option>
            <option value="blockquote">Quote Block</option>
          </select>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-btn-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("bold")}
            disabled={isCodeView}
            title="Bold"
          >
            <i className="bi bi-type-bold"></i>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("italic")}
            disabled={isCodeView}
            title="Italic"
          >
            <i className="bi bi-type-italic"></i>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("underline")}
            disabled={isCodeView}
            title="Underline"
          >
            <i className="bi bi-type-underline"></i>
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-btn-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("insertUnorderedList")}
            disabled={isCodeView}
            title="Bulleted List"
          >
            <i className="bi bi-list-ul"></i>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("insertOrderedList")}
            disabled={isCodeView}
            title="Numbered List"
          >
            <i className="bi bi-list-ol"></i>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => handleFormatBlock("blockquote")}
            disabled={isCodeView}
            title="Quote"
          >
            <i className="bi bi-quote"></i>
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-btn-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleInsertLink}
            disabled={isCodeView}
            title="Insert Link"
          >
            <i className="bi bi-link-45deg"></i>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => executeCmd("removeFormat")}
            disabled={isCodeView}
            title="Clear Formatting"
          >
            <i className="bi bi-eraser"></i>
          </button>
        </div>

        <div className="toolbar-spacer"></div>

        <div className="toolbar-btn-group">
          <button
            type="button"
            className={`toolbar-btn toggle-view-btn ${isCodeView ? "active" : ""}`}
            onClick={() => setIsCodeView(!isCodeView)}
            title={isCodeView ? "Switch back to Visual Editor" : "View raw HTML code"}
          >
            {isCodeView ? (
              <>
                <i className="bi bi-eye me-1"></i> Visual Editor
              </>
            ) : (
              <>
                <i className="bi bi-code-slash me-1"></i> View HTML Source
              </>
            )}
          </button>
        </div>
      </div>

      {/* EDITING AREA */}
      {isCodeView ? (
        <textarea
          className="rich-code-editor"
          value={internalHtml}
          onChange={handleCodeChange}
          placeholder="Type or paste raw HTML here..."
          rows="14"
        />
      ) : (
        <div
          ref={editorRef}
          className="rich-visual-editor"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          placeholder={placeholder}
        />
      )}

      {/* FOOTER STATS */}
      <div className="rich-editor-footer">
        <span className="editor-mode-label">
          {isCodeView ? (
            <span className="text-warning">
              <i className="bi bi-exclamation-triangle me-1"></i> Raw HTML Mode Active
            </span>
          ) : (
            <span>
              <i className="bi bi-check-circle-fill text-success me-1"></i> Visual Mode: Format naturally without seeing HTML tags
            </span>
          )}
        </span>
        <span className="editor-stats-badge">
          ~{readTimeMins} min read &bull; {wordCount} words
        </span>
      </div>
    </div>
  );
};

/* ========================================================
   MAIN ADMIN BLOG COMPONENT
   ======================================================== */
const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Modal State & Active Tab
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("content"); // "content" | "seo"
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "Spirituality",
    author: "Vaithikasri Editorial",
    author_bio: "The Vaithikasri Editorial Team comprises Vedic scholars, traditional purohits, and researchers dedicated to preserving and disseminating authentic Hindu traditions, rituals, and philosophy.",
    excerpt: "",
    content: "",
    status: "published",
    meta_title: "",
    meta_description: "",
    featured_image: ""
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BLOG_API}?admin=1`);
      const data = await res.json();
      if (data.status === "success") {
        setPosts(data.data || []);
      } else {
        setError(data.message || "Failed to load posts");
      }
    } catch (e) {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3500);
  };

  const handleOpenCreateModal = () => {
    setEditingPost(null);
    setActiveTab("content");
    setFormData({
      title: "",
      category: "Spirituality",
      author: "Vaithikasri Editorial",
      author_bio: "The Vaithikasri Editorial Team comprises Vedic scholars, traditional purohits, and researchers dedicated to preserving and disseminating authentic Hindu traditions, rituals, and philosophy.",
      excerpt: "",
      content: "<h2>Introduction</h2><p>Write your introduction here...</p><h2>Key Spiritual Significance</h2><p>Explain the traditional meaning and significance...</p>",
      status: "published",
      meta_title: "",
      meta_description: "",
      featured_image: ""
    });
    setShowModal(true);
  };

  const handleOpenEditModal = async (post) => {
    setEditingPost(post);
    setActiveTab("content");
    try {
      const res = await fetch(`${BLOG_API}/${post.slug}`);
      const data = await res.json();
      if (data.status === "success" && data.data) {
        const full = data.data;
        setFormData({
          id: full.id,
          title: full.title || "",
          category: full.category || "Spirituality",
          author: full.author || "Vaithikasri Editorial",
          author_bio: full.author_bio || "",
          featured_image: full.featured_image || "",
          excerpt: full.excerpt || "",
          content: full.content || "",
          status: full.status || "draft",
          meta_title: full.meta_title || "",
          meta_description: full.meta_description || ""
        });
        setShowModal(true);
      }
    } catch (e) {
      alert("Failed to load post content for editing");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await fetch(`${BLOG_API}?action=toggle_status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (data.status === "success") {
        showNotification(data.message);
        fetchPosts();
      } else {
        alert(data.message || "Failed to toggle status");
      }
    } catch (e) {
      alert("Server error toggling status");
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      const res = await fetch(`${BLOG_API}?action=delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (data.status === "success") {
        showNotification("Article deleted successfully");
        fetchPosts();
      } else {
        alert(data.message || "Failed to delete");
      }
    } catch (e) {
      alert("Server error deleting post");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Article Title is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const action = editingPost ? "edit" : "";
      const res = await fetch(`${BLOG_API}${action ? `?action=${action}` : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.status === "success") {
        showNotification(editingPost ? "Article updated successfully!" : "New article published!");
        setShowModal(false);
        fetchPosts();
      } else {
        alert(data.message || "Save failed");
      }
    } catch (e) {
      alert("Server error saving article");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter posts
  const filteredPosts = posts.filter((p) => {
    const matchesSearch =
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.excerpt && p.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategory === "All" || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ["All", ...new Set(posts.map((p) => p.category).filter(Boolean))];

  return (
    <div className="admin-blog-container">
      {/* HEADER BAR */}
      <div className="admin-blog-header">
        <div>
          <h2>Articles &amp; Blog Management</h2>
          <p className="admin-blog-desc">
            Publish, edit, and organize spiritual expositions, temple traditions, and Vedic articles to drive organic search traffic and comply with Google AdSense content policies.
          </p>
        </div>
        <button onClick={handleOpenCreateModal} className="admin-blog-create-btn">
          <i className="bi bi-plus-lg"></i> Write New Article
        </button>
      </div>

      {/* NOTIFICATION */}
      {successMsg && <div className="admin-blog-toast">{successMsg}</div>}

      {/* CONTROLS */}
      <div className="admin-blog-controls">
        <div className="search-box">
          <i className="bi bi-search"></i>
          <input
            type="text"
            placeholder="Search articles by title or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="category-select">
          <label>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="admin-blog-loading">
          <i className="bi bi-arrow-repeat spin-icon me-2"></i> Loading articles...
        </div>
      ) : error ? (
        <div className="admin-blog-error">{error}</div>
      ) : filteredPosts.length === 0 ? (
        <div className="admin-blog-empty">No articles match your criteria.</div>
      ) : (
        <div className="admin-blog-table-card">
          <table className="admin-blog-table">
            <thead>
              <tr>
                <th style={{ width: "42%" }}>Article Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Read Time</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <div className="post-table-title">{post.title}</div>
                    <div className="post-table-slug">/blog/{post.slug}</div>
                  </td>
                  <td>
                    <span className="category-tag">{post.category}</span>
                  </td>
                  <td>{post.author || "Vaithikasri"}</td>
                  <td>{post.read_time || 4} min</td>
                  <td>
                    <button
                      onClick={() => handleToggleStatus(post.id)}
                      className={`status-badge-btn ${post.status === "published" ? "status-published" : "status-draft"}`}
                      title="Click to toggle status"
                    >
                      <i className={`bi ${post.status === "published" ? "bi-check-circle" : "bi-pencil-square"} me-1`}></i>
                      {post.status === "published" ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div className="admin-action-btn-group">
                      <button
                        onClick={() => handleOpenEditModal(post)}
                        className="btn-action btn-edit"
                        title="Edit Article"
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(post.id, post.title)}
                        className="btn-action btn-delete"
                        title="Delete Article"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================================
          REDESIGNED ARTICLE EDITOR MODAL (WYSIWYG & MODERN TABS)
          ======================================================== */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-dialog modern-article-dialog">
            {/* MODAL HEADER */}
            <div className="admin-modal-header modern-modal-header">
              <div>
                <span className="modal-header-badge">
                  {editingPost ? "EDITING ARTICLE" : "NEW ARTICLE"}
                </span>
                <h3 className="modal-header-title">
                  {editingPost ? formData.title || "Untitled Article" : "Create New Spiritual Article"}
                </h3>
              </div>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            {/* TAB SELECTOR */}
            <div className="modern-modal-tabs">
              <button
                type="button"
                className={`modal-tab-btn ${activeTab === "content" ? "active" : ""}`}
                onClick={() => setActiveTab("content")}
              >
                <i className="bi bi-file-earmark-richtext me-2"></i> Article Content &amp; Story
              </button>
              <button
                type="button"
                className={`modal-tab-btn ${activeTab === "seo" ? "active" : ""}`}
                onClick={() => setActiveTab("seo")}
              >
                <i className="bi bi-search me-2"></i> SEO &amp; Author Settings
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="modern-modal-form">
              <div className="modern-modal-scrollable-body">
                {/* TAB 1: STORY & CONTENT */}
                {activeTab === "content" && (
                  <div className="tab-pane-content">
                    {/* Article Title */}
                    <div className="form-group">
                      <label className="modern-label">
                        Article Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        className="modern-input title-input"
                        placeholder="e.g. Hindu Temple Architecture: A Guide to Dravidian Temples"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </div>

                    {/* Category, Status & Cover Image Row */}
                    <div className="form-row-3">
                      <div className="form-group">
                        <label className="modern-label">Spiritual Category</label>
                        <select
                          className="modern-input"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        >
                          <option value="Spirituality">Spirituality</option>
                          <option value="Culture & Heritage">Culture &amp; Heritage</option>
                          <option value="Rituals & Poojas">Rituals &amp; Poojas</option>
                          <option value="Festivals">Festivals</option>
                          <option value="Vedic Knowledge">Vedic Knowledge</option>
                          <option value="Education">Education &amp; Patashala</option>
                          <option value="Astrology">Vedic Astrology (Jyotish)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="modern-label">Publication Status</label>
                        <select
                          className="modern-input"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                          <option value="published">Published (Visible)</option>
                          <option value="draft">Draft (Hidden)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="modern-label">Cover Image Path / URL</label>
                        <div className="cover-image-input-wrap">
                          <input
                            type="text"
                            className="modern-input"
                            placeholder="/images/blog/..."
                            value={formData.featured_image}
                            onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                          />
                          {formData.featured_image && (
                            <div className="cover-thumb-preview" title="Cover Image Preview">
                              <img
                                src={formData.featured_image}
                                alt="Cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Summary / Excerpt */}
                    <div className="form-group">
                      <label className="modern-label">
                        Article Summary / Excerpt
                        <span className="label-hint"> (Shown on card previews and search results)</span>
                      </label>
                      <textarea
                        rows="2"
                        className="modern-input"
                        placeholder="A short, compelling summary of this article..."
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      />
                    </div>

                    {/* Rich Visual WYSIWYG Editor */}
                    <div className="form-group">
                      <label className="modern-label">
                        Article Content <span className="text-danger">*</span>
                        <span className="label-hint"> (Edit formatted text directly — no HTML tags needed!)</span>
                      </label>
                      <RichEditor
                        value={formData.content}
                        onChange={(html) => setFormData({ ...formData, content: html })}
                        placeholder="Compose your article with headings, paragraphs, and sacred wisdom..."
                      />
                    </div>
                  </div>
                )}

                {/* TAB 2: SEO & SETTINGS */}
                {activeTab === "seo" && (
                  <div className="tab-pane-seo">
                    <div className="seo-header-note">
                      <i className="bi bi-google me-2 text-primary"></i>
                      <span>Optimize this article for high organic rankings and Google AdSense readability.</span>
                    </div>

                    {/* Google Snippet Live Preview */}
                    <div className="google-snippet-card">
                      <div className="snippet-badge">LIVE GOOGLE SEARCH PREVIEW</div>
                      <div className="snippet-url">vaithikasri.com &rsaquo; blog &rsaquo; {editingPost ? editingPost.slug : "your-article-slug"}</div>
                      <div className="snippet-title">{formData.meta_title || formData.title || "Article Title Preview"}</div>
                      <div className="snippet-desc">
                        {formData.meta_description || formData.excerpt || "Article summary snippet as it will appear on Google search results pages..."}
                      </div>
                    </div>

                    <div className="form-row-2 mt-4">
                      <div className="form-group">
                        <label className="modern-label">Author Name</label>
                        <input
                          type="text"
                          className="modern-input"
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="modern-label">
                          Meta Title (SEO)
                          <span className="label-hint"> ({formData.meta_title.length}/60 chars)</span>
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          placeholder="Leave empty to use main title"
                          value={formData.meta_title}
                          onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="modern-label">
                        Meta Description (SEO)
                        <span className="label-hint"> ({formData.meta_description.length}/160 chars)</span>
                      </label>
                      <textarea
                        rows="3"
                        className="modern-input"
                        placeholder="Brief SEO description to attract search clicks..."
                        value={formData.meta_description}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label className="modern-label">Author Biography / Credentials</label>
                      <textarea
                        rows="3"
                        className="modern-input"
                        value={formData.author_bio}
                        onChange={(e) => setFormData({ ...formData, author_bio: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* STICKY MODAL FOOTER */}
              <div className="admin-modal-footer modern-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <i className="bi bi-arrow-repeat spin-icon me-1"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-1"></i> {editingPost ? "Save Changes" : "Publish Article"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
